import classNames from 'classnames';
import React, { useMemo, useCallback } from 'react';
import Lazyload from 'react-lazy-load';
import Checkbox from '../../wui-checkbox/src';
import { TransferItemProps } from './iTransfer';

const Item = (props: TransferItemProps) => {
    const {
        item,
        lazy,
        checked,
        prefixCls,
        onClick,
        renderedText,
        renderedEl,
        showCheckbox,
        checkedKeys,
        isMultiDragSource,
        draggingItemId
    } = props;

    if (!item) return null;

    // 缓存拖拽附加状态计算
    const isAttachedDraggingItem = useMemo(() =>
        checked && !isMultiDragSource && draggingItemId,
    [checked, isMultiDragSource, draggingItemId]
    );

    // 缓存 className 计算，避免每次都调用 classNames
    const className = useMemo(() => classNames({
        [`${prefixCls}-content-item`]: true,
        [`${prefixCls}-content-item-disabled`]: item.disabled || isAttachedDraggingItem,
        [`${prefixCls}-content-item-selected`]: checked && !isAttachedDraggingItem
    }), [prefixCls, item.disabled, isAttachedDraggingItem, checked]);

    // 缓存点击处理器，避免每次渲染创建新函数
    const handleClick = useCallback(() => {
        if (!item.disabled && onClick) {
            onClick(item);
        }
    }, [item, onClick]);

    // 缓存多选拖拽计数
    const multiDragCount = useMemo(() =>
        isMultiDragSource && (checkedKeys as string[]).length > 1
            ? (checkedKeys as string[]).length
            : null,
    [isMultiDragSource, checkedKeys]
    );

    // 缓存 Checkbox 组件
    const checkboxEl = useMemo(() => showCheckbox ? (
        <Checkbox
            checked={checked && !isAttachedDraggingItem}
            disabled={item.disabled || !!isAttachedDraggingItem}
            onClick={(item.disabled || !onClick) ? undefined : handleClick}
        />
    ) : null, [showCheckbox, checked, isAttachedDraggingItem, item.disabled, onClick, handleClick]);

    // 缓存内容元素
    const contentEl = useMemo(() => (
        <>
            {checkboxEl}
            <span>{renderedEl}</span>
            {multiDragCount && (
                <span className="multi-drag-count">{multiDragCount}</span>
            )}
        </>
    ), [checkboxEl, renderedEl, multiDragCount]);

    // 缓存 li 元素
    const liEl = useMemo(() => (
        <li
            className={className}
            title={renderedText}
            onClick={(item.disabled || !onClick) ? undefined : handleClick}
        >
            {contentEl}
        </li>
    ), [className, renderedText, item.disabled, onClick, handleClick, contentEl]);

    // 缓存 lazy 配置判断
    const shouldUseLazy = useMemo(() =>
        lazy && lazy.container !== "modal",
    [lazy]
    );

    // 缓存 lazyProps 对象，避免每次创建新对象
    const lazyProps = useMemo(() => Object.assign({
        height: 32,
        offset: 500,
        throttle: 0,
        debounce: false,
    }, lazy), [lazy]);

    // 如果不使用 lazy，直接返回 li 元素
    if (!shouldUseLazy) {
        return liEl;
    }

    return <Lazyload {...lazyProps}>{liEl}</Lazyload>;
}

// 精确的 memo 比较函数，只比较影响渲染的关键 props
function areEqual(prevProps: TransferItemProps, nextProps: TransferItemProps) {
    // 比较核心标识
    if (prevProps.item?.key !== nextProps.item?.key) return false;

    // 比较影响显示的状态
    if (prevProps.checked !== nextProps.checked) return false;
    if (prevProps.item?.disabled !== nextProps.item?.disabled) return false;

    // 比较渲染内容
    if (prevProps.renderedText !== nextProps.renderedText) return false;
    if (prevProps.renderedEl !== nextProps.renderedEl) return false;

    // 比较 UI 配置
    if (prevProps.showCheckbox !== nextProps.showCheckbox) return false;
    if (prevProps.prefixCls !== nextProps.prefixCls) return false;

    // 比较拖拽相关状态
    if (prevProps.isMultiDragSource !== nextProps.isMultiDragSource) return false;
    if (prevProps.draggingItemId !== nextProps.draggingItemId) return false;

    // 如果是多选拖拽源，需要检查 checkedKeys 长度变化（用于显示计数）
    if (prevProps.isMultiDragSource && nextProps.isMultiDragSource) {
        const prevCount = prevProps.checkedKeys?.length || 0;
        const nextCount = nextProps.checkedKeys?.length || 0;
        if (prevCount !== nextCount) return false;
    }

    // 比较 lazy 配置（只比较引用，避免深度比较）
    if (prevProps.lazy !== nextProps.lazy) return false;

    // 所有关键 props 相同，不需要重新渲染
    return true;
}

export default React.memo(Item, areEqual);
